import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { safelyCreateUser } from './multiAuthHandler';

/**
 * Crea un nuevo usuario sin afectar la sesión actual del administrador
 * @param {Object} userData - Datos del usuario a crear
 * @param {string} password - Contraseña para el nuevo usuario
 * @param {string} adminEmail - Email del administrador que crea el usuario
 * @param {boolean} preventLogout - Si es verdadero, previene el cierre de sesión del administrador
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const createNewUserWithoutSession = async (userData, password, adminEmail, preventLogout = false) => {
  try {
    // Si estamos previniendo logout, establecer una bandera
    if (preventLogout) {
      localStorage.setItem('creatingUser', 'true');
      localStorage.setItem('adminEmail', adminEmail);
      localStorage.setItem('adminRole', 'Admin'); // Guardar temporalmente el rol
    }

    // Comprobar si el usuario ya existe en Firestore
    const userDocRef = doc(db, 'usuarios', userData.email);
    const userDoc = await getDoc(userDocRef);
    
    if (userDoc.exists()) {
      // Limpiar flags incluso en caso de error
      if (preventLogout) {
        localStorage.removeItem('creatingUser');
        localStorage.removeItem('adminEmail');
        localStorage.removeItem('adminRole');
      }
      return { 
        success: false, 
        error: "Ya existe un usuario con ese email en el sistema" 
      };    }
      // Usar el método más seguro para crear el usuario
    let uid;
    
    try {
      // Crear el usuario con Firebase Auth usando método seguro
      const result = await safelyCreateUser(userData.email, password);
      
      if (!result.success) {
        // Limpiar flags en caso de error
        if (preventLogout) {
          localStorage.removeItem('creatingUser');
          localStorage.removeItem('adminEmail');
          localStorage.removeItem('adminRole');
        }
        
        // Manejar específicamente el error de email ya en uso
        if (result.error.code === 'auth/email-already-in-use') {
          return {
            success: false,
            error: "Este email ya está registrado en Firebase Auth. Use un email diferente o contacte al administrador para recuperar la cuenta existente."
          };
        }
        
        return {
          success: false,
          error: result.error.message || "Error al crear la cuenta de usuario"
        };
      }
      
      uid = result.user.uid;
    } catch (authError) {
      // Manejar específicamente el error de email ya en uso
      if (authError.code === 'auth/email-already-in-use') {
        // Limpiar flags en caso de error
        if (preventLogout) {
          localStorage.removeItem('creatingUser');
          localStorage.removeItem('adminEmail');
          localStorage.removeItem('adminRole');
        }
        return {
          success: false,
          error: "Este email ya está registrado en Firebase Auth. Use un email diferente o contacte al administrador para recuperar la cuenta existente."
        };
      }
      // Re-lanzar otros errores de autenticación
      throw authError;
    }      // Obtener el UID asignado por Firebase Auth
    // uid ya está definido arriba en el try-catch
    
    // Crear la estructura de datos del usuario
    const userDataForFirestore = {
      ...userData,
      uid: uid,
      status: 1,
      createdAt: new Date().toISOString(),
      updatedAt: serverTimestamp(),
      createdBy: adminEmail
    };
    
    // Guardar en Firestore usando el email como ID del documento
    await setDoc(userDocRef, userDataForFirestore);
    
    // Limpiar flags al terminar correctamente
    if (preventLogout) {
      localStorage.removeItem('creatingUser');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminRole');
    }
    
    return {
      success: true,
      userId: userData.email,
      uid: uid,
      message: `Usuario ${userData.nombre} creado correctamente`
    };
  } catch (error) {
    console.error("Error al crear usuario:", error);
    
    // Limpiar flags en caso de error
    if (preventLogout) {
      localStorage.removeItem('creatingUser');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminRole');
    }
    
    return {
      success: false,
      error: error.message || "Error al crear usuario"
    };
  }
};

/**
 * Función para mantener la sesión del administrador después de crear un usuario
 * @param {string} adminEmail - Email del administrador
 * @param {string} adminPassword - Contraseña del administrador
 */
export const restoreAdminSession = async (adminEmail, adminPassword) => {
  try {
    const auth = getAuth();
    await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
    return true;
  } catch (error) {
    console.error("Error al restaurar sesión de administrador:", error);
    return false;
  }
};

/**
 * Verifica si un usuario tiene permisos de administrador
 * @param {string} email - Email del usuario a verificar
 * @returns {Promise<boolean>} - true si el usuario es administrador, false en caso contrario
 */
export const checkAdminPermissions = async (email) => {
  try {
    // Verificar el flag de creación en progreso
    const creatingUser = localStorage.getItem('creatingUser');
    const storedAdminEmail = localStorage.getItem('adminEmail');
    const storedAdminRole = localStorage.getItem('adminRole');
    
    // Si estamos en medio de crear un usuario y el email coincide con el admin almacenado
    if (creatingUser && email === storedAdminEmail && storedAdminRole === 'Admin') {
      console.log("Verificación de admin omitida durante creación de usuario");
      return true;
    }
    
    // Verificación normal desde Firestore
    const userDocRef = doc(db, 'usuarios', email);
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      return false;
    }
    
    const userData = userDoc.data();
    return userData.rol === 'Admin';
  } catch (error) {
    console.error("Error al verificar permisos de administrador:", error);
    return false;
  }
};

/**
 * Intenta obtener el UID de un usuario a través de una llamada de autenticación
 * @param {string} userEmail - Email del usuario
 * @param {string} password - Contraseña a intentar 
 * @returns {Promise<string|null>} El UID del usuario o null si no se puede obtener
 */
export const getUserUIDFromAuth = async (userEmail, password) => {
  try {
    // Crear una instancia secundaria de autenticación
    const tempAuth = getAuth();
    
    // Intentar iniciar sesión para obtener el UID
    const userCredential = await signInWithEmailAndPassword(tempAuth, userEmail, password)
      .catch(() => null);
      
    // Si el login tuvo éxito, obtener el UID y cerrar sesión
    if (userCredential && userCredential.user) {
      const uid = userCredential.user.uid;
      await tempAuth.signOut();
      return uid;
    }
    
    return null;
  } catch (error) {
    console.error("Error al obtener UID:", error);
    return null;
  }
};

/**
 * Esta función ha sido trasladada a tempPasswordReset.js para solucionar problemas de sintaxis.
 * Se mantiene aquí como referencia para compatibilidad, pero no debe usarse.
 * 
 * @deprecated Use la función resetUserPassword de tempPasswordReset.js en su lugar
 */
export const resetUserPassword = async (userEmail, newPassword) => {
  console.warn("Esta función está obsoleta. Use resetUserPassword de tempPasswordReset.js");
  
  try {
    // Importar dinámicamente la función desde el nuevo archivo
    const { resetUserPassword: actualResetFunction } = await import('./tempPasswordReset');
    return await actualResetFunction(userEmail, newPassword);
  } catch (error) {
    console.error("Error al redireccionar la función:", error);
    return {
      success: false,
      error: "Error al restablecer la contraseña. La función ha sido trasladada."
    };
  }
};
